import { useState, useEffect } from 'react';
import { ThemeProvider, useTheme } from 'emotion-theming';
import { css } from '@emotion/core';
import Loading from '../../components/Loading';
import { useUserSessionContext } from '../../components/UserSessionContextWrapper';
import useMessageService from '../../services/i18n/useMessageService';
import getStudiesData from '../../services/getStudiesData';
import sortStudiesData from '../../services/sortStudiesData';
import { isMobile } from '../../services/DeviceInformation';
import LocalStorageItems from '../../constants/LocalStorageItems';
import StudiesDataSortOptions from '../../constants/StudiesDataSortOptions';
import { userGeneralInfo } from '../my-account/myAccountApi';
import StudiesSection from './StudiesSection';
import StudiesTitleMobile from './StudiesTitleMobile';
import StudiesTitleDesktop from './StudiesTitleDesktop';

const theme = {
    linkFontSize: '1rem',
    textInputHeight: '2.3333rem',
    textInputFontSize: '1rem',
    textInputBorderRadius: '2px',
    textInputSpacing: '0',
    textInputSpacingVariant1: '0.6666rem',
};

export default function StudiesContent() {
    const { handleResponseSessionErrors } = useUserSessionContext();
    const { grayLighter } = useTheme();
    const [
        [
            myStudies,
            searchPlaceholder,
            lastLoginTimeLabel,
            companyNameLabel,
            noMatchesLabel,
            showingStudiesLabel,
            helpTextLabel,
            helpLinkLabel,
        ],
        messagesLoading,
    ] = useMessageService([
        'home.my_studies',
        'general.search',
        'home.last_login_time',
        'home.company_name',
        'general.no_matches_found',
        'home.showing_studies',
        'home.studies_help_message',
        'home.studies_help_link',
    ]);

    const [loading, setLoading] = useState(true);
    const [initialData, setInitialData] = useState([]);
    const [language, setLanguage] = useState();
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOption, setSortOption] = useState(
        localStorage.getItem(LocalStorageItems.STUDIESDATASORT) || StudiesDataSortOptions.LASTLOGIN,
    );

    useEffect(() => {
        (async () => {
            const studiesData = await getStudiesData();
            setInitialData(studiesData);

            const res = await userGeneralInfo();
            if (handleResponseSessionErrors(res)) {
                return;
            }

            setLanguage(res?.data?.language);

            setLoading(false);
        })();
    }, [handleResponseSessionErrors]);

    const handleSearch = (newTerm) => {
        setSearchTerm(newTerm);
    };

    const handleSortChange = (value) => {
        localStorage.setItem(LocalStorageItems.STUDIESDATASORT, value);
        setSortOption(value);
    };

    const filterAndSearch = (term, data) => {
        const newSortedData = sortStudiesData(data);
        const newFilteredSortedData = newSortedData.filter((section) => {
            return (
                section?.companyName?.toLowerCase()?.includes(term?.toLowerCase()) ||
                section?.studies?.some((study) =>
                    study.studyName?.toLowerCase()?.includes(term?.toLowerCase()),
                )
            );
        });

        return newFilteredSortedData;
    };

    const filteredData = filterAndSearch(searchTerm, initialData);

    const title = isMobile() ? (
        <StudiesTitleMobile
            searchTerm={searchTerm}
            handleSearch={handleSearch}
            searchPlaceholder={searchPlaceholder}
            handleSortChange={handleSortChange}
            sortOption={sortOption}
            lastLoginTimeLabel={lastLoginTimeLabel}
            companyNameLabel={companyNameLabel}
            loading={loading}
        />
    ) : (
        <StudiesTitleDesktop
            myStudies={myStudies}
            searchTerm={searchTerm}
            handleSearch={handleSearch}
            searchPlaceholder={searchPlaceholder}
            handleSortChange={handleSortChange}
            sortOption={sortOption}
            lastLoginTimeLabel={lastLoginTimeLabel}
            companyNameLabel={companyNameLabel}
            helpTextLabel={helpTextLabel}
            helpLinkLabel={helpLinkLabel}
            loading={loading}
        />
    );

    if (messagesLoading) {
        return null;
    }

    if (loading) {
        return (
            <ThemeProvider theme={theme}>
                <>{title}</>
                <Loading backgroundColorDisabled overlayMaskDisabled />
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={theme}>
            {title}
            {filteredData?.length === 0 && (
                <div
                    css={css`
                        flex-grow: 1;
                        text-align: center;
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                    `}
                >
                    <h2
                        css={css`
                            font-size: 2rem;
                            margin: 0 0 1.3333rem 0;
                            color: ${grayLighter};
                        `}
                    >
                        {noMatchesLabel}
                    </h2>
                </div>
            )}
            {filteredData?.map((section) => (
                <StudiesSection
                    key={section.redirectUrl}
                    section={section}
                    language={language}
                    searchTerm={searchTerm}
                    showingStudiesLabel={showingStudiesLabel}
                />
            ))}
        </ThemeProvider>
    );
}
